/**
 * Eigene Mahlzeiten auf dem Gerät (16.10.2026): eine Mahlzeit an einem Tag
 * auslassen und wieder zurücknehmen.
 *
 * Die Kennung ist fest je Konto, Tag und Mahlzeit (`mealSkipId`): Lassen zwei
 * Geräte dieselbe Mahlzeit aus, ist es dieselbe Zeile; Zurücknehmen löscht
 * weich, erneutes Auslassen belebt sie wieder.
 */

import { mealSkipId } from '@kraftwerk/shared';
import type { DaySlot } from '@kraftwerk/shared';
import { currentOwnerId } from '../auth.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalMealSkip } from './local.js';
import { stamp, writeRow } from './write.js';

/** Ausgelassene Mahlzeiten eines Tages. */
export async function listSkips(
  date: string,
  database: KraftwerkDatabase = db,
): Promise<LocalMealSkip[]> {
  return (await database.meal_skips.where('date').equals(date).toArray()).filter(
    (row) => !row.deletedAt,
  );
}

/** Ohne bekanntes Konto gibt es keine feste Kennung — dann passiert nichts. */
export async function skipMeal(
  date: string,
  slot: DaySlot,
  database: KraftwerkDatabase = db,
  ownerId: string | null = currentOwnerId(),
): Promise<LocalMealSkip | null> {
  if (!ownerId) return null;
  const now = stamp();
  const row: LocalMealSkip = {
    id: mealSkipId(ownerId, date, slot),
    updatedAt: now,
    deletedAt: null,
    date,
    slot,
  };
  return writeRow('meal_skips', row, 'upsert', database);
}

export async function unskipMeal(
  date: string,
  slot: DaySlot,
  database: KraftwerkDatabase = db,
  ownerId: string | null = currentOwnerId(),
): Promise<void> {
  if (!ownerId) return;
  const existing = await database.meal_skips.get(mealSkipId(ownerId, date, slot));
  if (!existing || existing.deletedAt) return;
  const now = stamp();
  await writeRow('meal_skips', { ...existing, deletedAt: now, updatedAt: now }, 'delete', database);
}
